import React from "react";
import styled from "styled-components";
import { Text, theme } from "@ifgengineering/component-library";
import { CurrencyEnumType } from "@ifgengineering/client-invest-sdk";
import ProgressBar from "@components/ProgressBar";
import useCurrency from "@helpers/useCurrency";
import { DealFundraisingCapacity, isGaugeVisible } from "@state/dealCapacity";

interface FundraisingCapacityCardProps {
  capacity: DealFundraisingCapacity;
  currency?: CurrencyEnumType;
  /** Render without the card surface (a thin divider instead). */
  embedded?: boolean;
  testId?: string;
}

const Container = styled.div<{ embedded: boolean }>`
  ${({ embedded }) =>
    embedded
      ? `
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid ${theme.colors.SLATE200};
  `
      : `
    background-color: ${theme.colors.WHITE};
    border-radius: 12px;
    padding: 16px;
  `}
`;

const BarWrapper = styled.div`
  margin: 8px 0;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const SubWrap = styled.div`
  margin-top: 8px;
`;

// Subscribed progress for a capped deal; closed-round notice when the round closed.
const FundraisingCapacityCard: React.FC<FundraisingCapacityCardProps> = ({
  capacity,
  currency = "GBP",
  embedded = false,
  testId = "fundraising-capacity-card",
}) => {
  const { toCurrency } = useCurrency(currency, { noDecimals: true });
  const { fundraisingCap, remainingCapacity, windowStart } = capacity;

  // No active round (windowStart null) means the round has closed.
  if (windowStart == null) {
    return (
      <Container embedded={embedded} data-testid={testId}>
        <Text type="S14" color="SLATE700">
          Fundraising round closed
        </Text>
        <SubWrap>
          <Text type="P14" color="SLATE600">
            No longer accepting new investment
          </Text>
        </SubWrap>
      </Container>
    );
  }

  if (fundraisingCap == null) {
    return null;
  }

  const cap = fundraisingCap;
  const remaining = Math.max(remainingCapacity ?? 0, 0);
  const raised = Math.max(cap - remaining, 0);
  const pct = cap > 0 ? Math.round((raised / cap) * 100) : 0;

  return (
    <Container embedded={embedded} data-testid={testId}>
      <Text type="S14" color="SLATE700">
        Fundraising Limit - {toCurrency(cap)}
      </Text>
      {isGaugeVisible(capacity) && (
        <>
          <BarWrapper>
            <ProgressBar percentage={pct} barColor="GREEN600" barBgColor="SLATE200" />
          </BarWrapper>
          <Row>
            <Text type="P14" color="SLATE600">
              {toCurrency(remaining)} left
            </Text>
            <Text type="P14" color="SLATE600">
              {pct}%
            </Text>
          </Row>
        </>
      )}
    </Container>
  );
};

export default FundraisingCapacityCard;
